import {
  Box,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  makeStyles,
  Popover,
  TextField,
  Typography,
} from '@material-ui/core';
import ScheduleIcon from '@material-ui/icons/Schedule';
import React, { FC, ReactNode, useEffect, useState } from 'react';
import { Theme } from 'src/theme';
import {
  DurationParts,
  formatDuration,
  partsToSeconds,
  secondsToParts,
} from 'src/utils/duration';

export interface DurationPreset {
  label: string;
  seconds: number;
}

const DEFAULT_PRESETS: DurationPreset[] = [
  { label: '1 min', seconds: 60 },
  { label: '15 min', seconds: 900 },
  { label: '1 hour', seconds: 3600 },
  { label: '10 hours', seconds: 36000 },
  { label: '24 hours', seconds: 86400 },
];

interface DurationPickerProps {
  label: string;
  value: number;
  onChange: (_totalSeconds: number) => void;
  onBlur?: () => void;
  helperText?: ReactNode;
  error?: boolean;
  id?: string;
  presets?: DurationPreset[];
}

interface UnitFieldConfig {
  key: keyof DurationParts;
  label: string;
  max?: number;
}

const UNIT_FIELDS: UnitFieldConfig[] = [
  { key: 'days', label: 'Days' },
  { key: 'hours', label: 'Hours', max: 23 },
  { key: 'minutes', label: 'Minutes', max: 59 },
  { key: 'seconds', label: 'Seconds', max: 59 },
];

const useStyles = makeStyles((theme: Theme) => ({
  popoverContent: {
    padding: theme.spacing(2),
    width: 360,
    maxWidth: '90vw',
  },
  presetRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  unitRow: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  unitField: {
    flex: 1,
  },
}));

const clampUnit = (raw: string, max?: number): number => {
  const parsed = Math.floor(Number(raw));
  if (!Number.isFinite(parsed) || parsed < 0) return 0;
  if (max !== undefined && parsed > max) return max;
  return parsed;
};

const DurationPicker: FC<DurationPickerProps> = ({
  label,
  value,
  onChange,
  onBlur,
  helperText,
  error,
  id,
  presets = DEFAULT_PRESETS,
}) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [parts, setParts] = useState<DurationParts>(() => secondsToParts(value));

  const open = Boolean(anchorEl);

  // Resync from the canonical value when the popover opens.
  useEffect(() => {
    if (open) {
      setParts(secondsToParts(value));
    }
  }, [open, value]);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    if (onBlur) onBlur();
  };

  const commit = (next: DurationParts) => {
    setParts(next);
    onChange(partsToSeconds(next));
  };

  const handleUnitChange = (key: keyof DurationParts, max: number | undefined) => (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    commit({ ...parts, [key]: clampUnit(event.target.value, max) });
  };

  const handlePreset = (seconds: number) => {
    commit(secondsToParts(seconds));
  };

  const totalSeconds = partsToSeconds(parts);

  return (
    <>
      <TextField
        id={id}
        fullWidth
        variant="outlined"
        label={label}
        value={formatDuration(value)}
        onClick={handleOpen}
        error={error}
        helperText={helperText}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              <IconButton edge="end" size="small" aria-label="edit duration" onClick={handleOpen}>
                <ScheduleIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        }}
        inputProps={{ style: { cursor: 'pointer' } }}
      />
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ style: { overflow: 'visible' } }}
      >
        <Box className={classes.popoverContent}>
          <Typography variant="subtitle2" color="textSecondary" gutterBottom>
            Quick set
          </Typography>
          <Box className={classes.presetRow}>
            {presets.map((preset) => (
              <Chip
                key={preset.seconds}
                label={preset.label}
                clickable
                size="small"
                color={totalSeconds === preset.seconds ? 'primary' : 'default'}
                onClick={() => handlePreset(preset.seconds)}
              />
            ))}
          </Box>
          <Divider />
          <Box className={classes.unitRow} mt={2}>
            {UNIT_FIELDS.map((field) => (
              <TextField
                key={field.key}
                className={classes.unitField}
                type="number"
                label={field.label}
                variant="outlined"
                size="small"
                value={String(parts[field.key])}
                onChange={handleUnitChange(field.key, field.max)}
                inputProps={{ min: 0, max: field.max }}
              />
            ))}
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default DurationPicker;
